import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { getRiskSettings, saveRiskSettings, calculateRiskMetrics } from '@/utils/riskManager';
import { getTradeJournal } from '@/utils/localStorage';
import { toast } from 'sonner';
import { Shield, TrendingUp, TrendingDown, AlertCircle, DollarSign, Activity } from 'lucide-react';

export default function RiskManager() {
  const [settings, setSettings] = useState({
    accountBalance: 10000,
    riskPerTrade: 1.0,
    maxOpenTrades: 3,
    maxDailyLoss: 5.0,
    enabled: true
  });
  
  const [metrics, setMetrics] = useState(null);
  
  useEffect(() => {
    loadSettings();
    loadMetrics();
  }, []);
  
  const loadSettings = () => {
    const saved = getRiskSettings();
    if (saved) {
      setSettings(saved);
    }
  };
  
  const loadMetrics = () => {
    const trades = getTradeJournal();
    const savedSettings = getRiskSettings();
    const calculated = calculateRiskMetrics(savedSettings?.accountBalance || 10000, trades);
    setMetrics(calculated);
  };
  
  const handleSave = () => {
    try {
      saveRiskSettings(settings);
      toast.success('Risk settings saved successfully');
      loadMetrics();
    } catch (error) {
      toast.error('Failed to save risk settings');
    }
  };
  
  const getRemainingRiskCapacity = () => {
    if (!metrics) return 100;
    const used = Math.abs(parseFloat(metrics.dailyPnL));
    const capacity = ((settings.maxDailyLoss - used) / settings.maxDailyLoss) * 100;
    return Math.max(0, capacity);
  };
  
  const getCapacityColor = (capacity) => {
    if (capacity >= 70) return 'text-success';
    if (capacity >= 40) return 'text-warning';
    return 'text-destructive';
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Shield className="w-10 h-10 text-primary" />
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
            Risk Manager
          </h1>
        </div>
        <p className="text-muted-foreground font-medium">
          Control trading risk with automated position sizing and limit enforcement.
        </p>
      </div>
      
      <div className="mb-6">
        <Card className="p-6 border-2 border-primary/30 bg-primary/5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-foreground mb-1">Risk Management Status</h3>
              <p className="text-sm text-muted-foreground">
                {settings.enabled ? 'Risk limits are active and enforced' : 'Risk management is disabled'}
              </p>
            </div>
            <Switch
              data-testid="risk-enabled-toggle"
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </Card>
      </div>
      
      {!settings.enabled && (
        <Card className="p-6 border-destructive/50 bg-destructive/10 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-destructive mb-1">Risk Management Disabled</h4>
              <p className="text-sm text-foreground">
                Enable risk management to automatically calculate position sizes and enforce trading limits.
              </p>
            </div>
          </div>
        </Card>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6 border-border/40 bg-card">
          <h2 className="text-xl font-bold text-foreground mb-4">Global Risk Settings</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="accountBalance" className="text-foreground font-semibold mb-2 block">
                Account Balance ($)
              </Label>
              <Input
                id="accountBalance"
                data-testid="account-balance-input"
                type="number"
                step="0.01"
                value={settings.accountBalance}
                onChange={(e) => setSettings(prev => ({ ...prev, accountBalance: parseFloat(e.target.value) }))}
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Your total trading capital</p>
            </div>
            
            <div>
              <Label htmlFor="riskPerTrade" className="text-foreground font-semibold mb-2 block">
                Risk Per Trade (%)
              </Label>
              <Input
                id="riskPerTrade"
                data-testid="risk-per-trade-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.riskPerTrade}
                onChange={(e) => setSettings(prev => ({ ...prev, riskPerTrade: parseFloat(e.target.value) }))}
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Recommended: 1-2% per trade</p>
            </div>
            
            <div>
              <Label htmlFor="maxOpenTrades" className="text-foreground font-semibold mb-2 block">
                Max Open Trades
              </Label>
              <Input
                id="maxOpenTrades"
                data-testid="max-open-trades-input"
                type="number"
                min="1"
                max="20"
                value={settings.maxOpenTrades}
                onChange={(e) => setSettings(prev => ({ ...prev, maxOpenTrades: parseInt(e.target.value) }))}
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Maximum simultaneous positions</p>
            </div>
            
            <div>
              <Label htmlFor="maxDailyLoss" className="text-foreground font-semibold mb-2 block">
                Max Daily Loss (%)
              </Label>
              <Input
                id="maxDailyLoss"
                data-testid="max-daily-loss-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.maxDailyLoss}
                onChange={(e) => setSettings(prev => ({ ...prev, maxDailyLoss: parseFloat(e.target.value) }))}
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Stop trading after this loss threshold</p>
            </div>
            
            <Button
              onClick={handleSave}
              data-testid="save-risk-settings-btn"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 mt-4"
            >
              <Shield className="w-4 h-4 mr-2" />
              Save Risk Settings
            </Button>
          </div>
        </Card>
        
        <div className="space-y-6">
          <Card className="p-6 border-border/40 bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Current Risk Metrics</h2>
            
            {metrics && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-md bg-secondary/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Account Balance</p>
                    </div>
                    <p className="text-2xl font-black font-mono text-foreground" data-testid="current-balance">
                      ${settings.accountBalance.toLocaleString()}
                    </p>
                  </div>
                  
                  <div className={`p-4 rounded-md ${parseFloat(metrics.dailyPnL) >= 0 ? 'bg-success/10' : 'bg-destructive/10'}`}>
                    <div className="flex items-center space-x-2 mb-1">
                      <Activity className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Daily PnL</p>
                    </div>
                    <p className={`text-2xl font-black font-mono ${parseFloat(metrics.dailyPnL) >= 0 ? 'text-success' : 'text-destructive'}`} data-testid="daily-pnl">
                      {parseFloat(metrics.dailyPnL) >= 0 ? '+' : ''}{metrics.dailyPnL}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ${parseFloat(metrics.dailyPnLDollars) >= 0 ? '+' : ''}{metrics.dailyPnLDollars}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-md bg-secondary/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Open Trades</p>
                    </div>
                    <p className="text-2xl font-black font-mono text-foreground" data-testid="open-trades-count">
                      {metrics.openTrades} / {settings.maxOpenTrades}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-md bg-secondary/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <Activity className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Today's Trades</p>
                    </div>
                    <p className="text-2xl font-black font-mono text-foreground" data-testid="todays-trades">
                      {metrics.todaysTradeCount}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {metrics.wins}W / {metrics.losses}L ({metrics.winRate}%)
                    </p>
                  </div>
                </div>
                
                <div className="p-4 rounded-md border border-border/40 bg-background">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-foreground">Remaining Risk Capacity</p>
                    <p className={`text-lg font-black font-mono ${getCapacityColor(getRemainingRiskCapacity())}`}>
                      {getRemainingRiskCapacity().toFixed(1)}%
                    </p>
                  </div>
                  <div className="w-full bg-secondary/50 rounded-full h-3 overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        getRemainingRiskCapacity() >= 70 ? 'bg-success' :
                        getRemainingRiskCapacity() >= 40 ? 'bg-warning' :
                        'bg-destructive'
                      }`}
                      style={{ width: `${getRemainingRiskCapacity()}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Daily loss limit: {Math.abs(parseFloat(metrics.dailyPnL)).toFixed(2)}% / {settings.maxDailyLoss}%
                  </p>
                </div>
              </div>
            )}
          </Card>
          
          <Card className="p-6 border-border/40 bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Position Size Calculator</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Based on your risk settings, recommended position size is calculated automatically in the Trade Validator.
            </p>
            <div className="p-4 rounded-md bg-primary/10 border border-primary/30">
              <p className="text-sm font-semibold text-foreground mb-2">Formula:</p>
              <code className="text-xs font-mono text-foreground block">
                Position Size = (Account Balance × Risk %) / Point Risk
              </code>
              <p className="text-xs text-muted-foreground mt-2">
                Risk Amount: ${(settings.accountBalance * settings.riskPerTrade / 100).toFixed(2)} per trade
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
